import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import ViewBlogModal from "shared/modal/viewBlogModal";
import { DeleteBlogService, GetAllBlogsService } from "shared/services/blogs";
import styles from "./style.module.scss";

interface AllBlogsProps {
  handleUpdate: (blog: BlogsData) => void;
  refresh: boolean;
  setRefreshBlogs: React.Dispatch<React.SetStateAction<boolean>>;
  uploadedFile: string | null;
}

interface Cover {
  id: number;
  type: string;
  mime: string;
  size: string;
  full_path: string;
}

export interface BlogsData {
  id: number;
  category: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  cover: Cover;
}

const BlogsTable = ({ handleUpdate, refresh }: AllBlogsProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    blog: BlogsData | null;
  }>({
    open: false,
    action: null,
    blog: null,
  });

  const [viewBlogModal, setViewBlogModal] = useState<{
    open: boolean;
    blog: BlogsData | null;
  }>({
    open: false,
    blog: null,
  });

  const openViewBlogModal = (blog: BlogsData) => {
    setViewBlogModal({ open: true, blog });
  };

  const closeViewBlogModal = () => {
    setViewBlogModal({ open: false, blog: null });
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [blogs, setBlogs] = useState<BlogsData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const openConfirmModal = (action: string, blog: BlogsData) => {
    if (action === "Update" && handleUpdate) {
      handleUpdate(blog);
    } else {
      setConfirmModal({ open: true, action, blog });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, blog: null });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllBlogs(page);
  };

  const blogsTableHeads = [
    { title: "Blogs Title", key: "title" },
    { title: "Category", key: "category" },
    { title: "Creation Date", key: "created_at" },
    { title: "Last Updated", key: "updated_at" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllBlogs = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllBlogsService(page, 10);
      const blogData = Array.isArray(response.data.data.data)
        ? response.data.data.data
        : [];
      setBlogs(blogData);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      toastMessage("error", "Failed to fetch blogs. Please try again.");
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    const { id } = confirmModal.blog || {};
    if (!id) return;

    setActionLoading(true);
    try {
      const response = await DeleteBlogService(id);
      if (response?.status === 200) {
        toastMessage("success", "Blog deleted successfully");
        handleGetAllBlogs(currentPage);
      } else {
        toastMessage("error", "Failed to delete the blog");
      }
    } catch (error) {
      toastMessage("error", "Error: Could not delete blog");
      console.error("Error deleting blog:", error);
    } finally {
      setActionLoading(false);
      closeConfirmModal();
    }
  };

  useEffect(() => {
    handleGetAllBlogs(currentPage);
    // eslint-disable-next-line
  }, [refresh]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={blogsTableHeads}
          title="Blogs List"
          isEmpty={!blogs.length}
          loading={loading}
        >
          {loading
            ? Array.from({ length: 5 }).map((_, index) => (
              <TableRowLoader key={index} />
            ))
            : blogs.map((data) => {
              return (
                <tr key={data.id}>
                  <td>{data.title}</td>
                  <td>{data.category}</td>
                  <td>{formatDate(data.created_at)}</td>
                  <td>{formatDate(data.updated_at)}</td>
                  <td className={classNames(styles.IconsContainer)}>
                    <CustomToolTip label="Detail">
                      <button onClick={() => openViewBlogModal(data)}>
                        <Icons.Eye />
                      </button>
                    </CustomToolTip>

                    <CustomToolTip label="Update">
                      <button
                        onClick={() => openConfirmModal("Update", data)}
                      >
                        <Icons.UpdateBlog />
                      </button>
                    </CustomToolTip>
                    <CustomToolTip label="Delete">
                      <button
                        onClick={() => openConfirmModal("Delete", data)}
                      >
                        <Icons.TrashOutline />
                      </button>
                    </CustomToolTip>
                  </td>
                </tr>
              );
            })}
        </CustomTable>

        <div className={classNames(styles.paginationContainer, "mt-3")}>
          <CustomPagination
            onPageChange={handlePageChange}
            totalCount={totalItems}
            currentPage={currentPage}
            pageSize={10}
          />
        </div>
      </div>

      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title={`Confirm ${confirmModal.action}`}
          loading={actionLoading}
          actionButtonText={`Yes, ${confirmModal.action}`}
          subtitle={`Are you sure you want to ${confirmModal.action?.toLowerCase()} this blog?`}
          handleSubmit={handleConfirmAction}
          handleClose={closeConfirmModal}
        />
      )}
      {viewBlogModal.open && viewBlogModal.blog && (
        <ViewBlogModal
          show={viewBlogModal.open}
          blogDetails={{
            title: viewBlogModal.blog.title,
            category: viewBlogModal.blog.category,
            cover_url: viewBlogModal.blog.cover?.full_path || "",
            content: viewBlogModal.blog.content,
            created_at: viewBlogModal.blog.created_at,
          }}
          handleClose={closeViewBlogModal}
          actionButtonText="Close"
        />
      )}
    </div>
  );
};
const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 5 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default BlogsTable;
